// Utility for caching and batch processing location data
import axios from "axios";
import { apiHost } from "../api/apiClient";

class LocationCache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.batchTimeout = null;
  }

  // Get a location from cache or queue it for batch retrieval
  async getAddress(lat, lng, callback) {
    const result = await this.getAddressDetails(lat, lng);
    const address = result ? result.address : null;
    if (callback) callback(address);
    return address;
  }

  async getAddressDetails(lat, lng, options = {}) {
    if (!lat || !lng) return null;
    
    const key = this.getLocationKey(lat, lng);

    if (options.bypassCache || options.forceRefresh) {
      return this.fetchAddress(lat, lng, key, false, options.forceRefresh);
    }
    
    // Return from cache if available
    if (this.cache.has(key)) {
      const cachedAddress = this.normalizeAddressResult(this.cache.get(key));
      return { ...cachedAddress, source: "browser-cache", originalSource: cachedAddress.source };
    }
    
    // Add to pending requests
    if (!this.pendingRequests.has(key)) {
      this.pendingRequests.set(key, []);
      this.scheduleBatchRequest();
    }
    
    // Return a promise that will resolve when the batch completes
    return new Promise((resolve) => {
      this.pendingRequests.get(key).push(resolve);
    });
  }

  normalizeAddressResult(result) {
    if (result && typeof result === "object") {
      return {
        address: result.address || result.Address || result.addressName || result.AddressName || "",
        source: result.source || result.Source || "",
        originalSource: result.originalSource || result.OriginalSource || "",
      };
    }

    return {
      address: result || "",
      source: "",
      originalSource: "",
    };
  }

  // Create a unique key for lat/lng pair
  getLocationKey(lat, lng) {
    return `${parseFloat(lat).toFixed(6)},${parseFloat(lng).toFixed(6)}`;
  }

  // Schedule a batch request with debouncing
  scheduleBatchRequest() {
    if (this.batchTimeout) clearTimeout(this.batchTimeout);
    
    this.batchTimeout = setTimeout(() => {
      this.processBatchRequest();
    }, 200); // Debounce for 200ms
  }

  // Process all pending location requests in one batch
  async processBatchRequest() {
    if (this.pendingRequests.size === 0) return;
    
    const requestKeys = Array.from(this.pendingRequests.keys());
    const locations = requestKeys.map(key => {
      const [lat, lng] = key.split(',');
      return { Latitude: parseFloat(lat), Longitude: parseFloat(lng), key };
    });
    
    try {
      const response = await axios.post(
        `${apiHost}/api/Assets/GetMultipleLocationAddresses?includeSource=true${locations.some(loc => loc.forceRefresh) ? "&forceRefresh=true" : ""}`,
        locations.map(loc => ({ Latitude: loc.Latitude, Longitude: loc.Longitude })),
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "application/json"
          },
        }
      );
      
      if (response.status === 200) {
        const addressesMap = response.data || {};
        
        // Update cache and resolve promises for each location
        for (const loc of locations) {
          const exactKey = `${loc.Latitude},${loc.Longitude}`;
          const fixedKey = `${parseFloat(loc.Latitude).toFixed(6)},${parseFloat(loc.Longitude).toFixed(6)}`;
          const addressResult = this.normalizeAddressResult(addressesMap[loc.key] || addressesMap[exactKey] || addressesMap[fixedKey] || null);
          const address = addressResult.address || null;

          if (address) {
            this.cache.set(loc.key, addressResult);
          }

          // Resolve all pending callbacks for this location, even when no address is found.
          const callbacks = this.pendingRequests.get(loc.key) || [];
          callbacks.forEach(callback => callback(addressResult));
          this.pendingRequests.delete(loc.key);
        }
        return;
      }

      await this.fetchAddressesIndividually(locations);
    } catch (error) {
      console.error("Error in batch location request");
      
      // If batch request fails, fall back to individual requests
      await this.fetchAddressesIndividually(locations);
    }
  }

  async fetchAddressesIndividually(locations) {
    const batch = locations.map(loc => this.fetchAddress(loc.Latitude, loc.Longitude, loc.key));
    await Promise.allSettled(batch);
  }

  // Fetch address for a single location and update cache
  async fetchAddress(lat, lng, key, resolvePending = true, forceRefresh = false) {
    try {
      const response = await axios.get(
        `${apiHost}/api/Assets/GetLocationAddress?Latitude=${lat}&Longitude=${lng}&includeSource=true${forceRefresh ? "&forceRefresh=true" : ""}`,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );
      
      if (response.status === 200) {
        const addressResult = this.normalizeAddressResult(response.data);
        this.cache.set(key, addressResult);
        
        // Resolve all pending callbacks for this location
        if (resolvePending) {
          const callbacks = this.pendingRequests.get(key) || [];
          callbacks.forEach(callback => callback(addressResult));
          this.pendingRequests.delete(key);
        }

        return addressResult;
      }
    } catch (error) {
      console.error("Error fetching address");
      
      // Resolve pending callbacks with null
      if (resolvePending) {
        const callbacks = this.pendingRequests.get(key) || [];
        callbacks.forEach(callback => callback(null));
        this.pendingRequests.delete(key);
      }
    }

    return null;
  }

  // Clear the cache
  clearCache() {
    this.cache.clear();
  }
}

// Create singleton instance
const locationCache = new LocationCache();
export default locationCache; 
